import { useEffect, useState } from 'react';
import Modal from '../../ui/Modal/Modal';
import Input from '../../ui/Input/Input';
import Button from '../../ui/Button/Button';
import styles from './SceneHeadingModal.module.css';

// Compose a Master-Scenes-style heading (INT./EXT. + LOCATION - TIME) and
// hand it back as a single uppercase string. Mirrors the shape of the scene
// composer in CreateChapterModal so writers see the same form whether the
// heading lives in a chapter title (scaletta) or inline in the body (plain).

const KINDS = ['INT', 'EXT', 'INT/EXT'];
const TIME_PRESETS = ['DAY', 'NIGHT', 'MORNING', 'EVENING', 'DAWN', 'DUSK', 'CONTINUOUS', 'LATER'];

export default function SceneHeadingModal({ isOpen, onClose, onSubmit }) {
  const [kind, setKind] = useState('INT');
  const [location, setLocation] = useState('');
  const [time, setTime] = useState('DAY');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setKind('INT');
    setLocation('');
    setTime('DAY');
    setError(null);
  }, [isOpen]);

  const compose = () =>
    `${kind}. ${location.trim().toUpperCase()} - ${time.trim().toUpperCase()}`;

  const submit = () => {
    if (!location.trim()) {
      setError('Location is required');
      return;
    }
    if (!time.trim()) {
      setError('Time of day is required');
      return;
    }
    onSubmit(compose());
  };

  const preview = location.trim() ? compose() : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Insert scene heading"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit}>Insert</Button>
        </>
      }
    >
      <div className={styles.field}>
        <span className={styles.label}>Scene type</span>
        <div className={styles.kindRow}>
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`${styles.kindBtn} ${kind === k ? styles.kindActive : ''}`}
              onClick={() => setKind(k)}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <Input
        label="Location"
        placeholder="e.g. KITCHEN, ABANDONED WAREHOUSE"
        value={location}
        onChange={(e) => { setLocation(e.target.value); setError(null); }}
        autoFocus
      />

      <div className={styles.field}>
        <br />
        <span className={styles.label}>Time of day</span>
        <Input
          placeholder="DAY / NIGHT / CONTINUOUS"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
        <div className={styles.presetRow}>
          {TIME_PRESETS.map((t) => (
            <button
              key={t}
              type="button"
              className={`${styles.preset} ${time === t ? styles.presetActive : ''}`}
              onClick={() => setTime(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {preview && (
        <div className={styles.preview}>
          <span className={styles.previewLabel}>Heading:</span>
          <span className={styles.previewValue}>{preview}</span>
        </div>
      )}

      {error && <span className={styles.error}>{error}</span>}
    </Modal>
  );
}
